import { useCallback, useEffect, useRef, useState } from "react";
import type { ConfigGravacao } from "./config.ts";

/* ===========================================================================
   Gravação da reunião que está acontecendo em OUTRA ABA (Meet, Teams, Zoom).

   Pedir que alguém grave por fora, ache o arquivo e suba depois é o mesmo que não
   ter gravação: some no meio do caminho. Aqui a pessoa clica em gravar, escolhe a
   aba e continua trabalhando.

   Duas trilhas, e é isso que dá a diarização:
     - `aba`  → getDisplayMedia, o áudio da reunião (o cliente e os outros).
                Só vem se quem escolher marcar "compartilhar áudio da aba".
     - `mic`  → getUserMedia, a voz de quem conduziu.
   Gravadas SEPARADAS e disparadas no mesmo instante — o servidor transcreve cada
   uma com marca de tempo e intercala. Sem modelo de voz, sem trocar duas vozes
   parecidas.

   O VÍDEO DA ABA NÃO É DESCARTADO quando a leitura de nome está ligada. Meet e
   Teams destacam o tile de quem fala e escrevem o nome nele: amostrando quadros
   durante a reunião, o servidor lê esses nomes e a transcrição sai com "Marina
   Alves" em vez de "Participantes". O vídeo em si nunca é enviado nem guardado —
   só quadros pequenos, e só quando há som vindo da aba E a imagem mudou.

   Chrome e Edge no computador fazem as duas coisas; Firefox e Safari não capturam
   áudio de aba, e aí sobra o microfone (o hook avisa).

   `estado`: 'ocioso' | 'preparando' | 'gravando' | 'enviando'. `preparando` existe
   como estado próprio porque o intervalo entre o clique e a escolha da aba é LONGO
   e, sem ele, a tela ficava idêntica a antes do clique — parecia que o botão não
   funcionou.
   =========================================================================== */

const BARRAS = 24;
const LARGURA_QUADRO = 960;
/** Teto de segurança: reunião de três horas não pode encher a memória da aba. */
const MAX_QUADROS = 220;
/** Abaixo disso a aba está muda — quadro de ninguém falando não diz quem falou. */
const SILENCIO = 0.06;

const VAZIO = () => Array.from({ length: BARRAS }, () => 0);

const MIMES = ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus", "audio/mp4"];
/* VP8 antes de VP9: tela de reunião é texto e rosto em movimento lento, onde os
   dois empatam em qualidade — e o VP8 codifica sem pesar na máquina de quem está
   NA reunião, que é a que menos pode engasgar. */
const MIMES_VIDEO = ["video/webm;codecs=vp8,opus", "video/webm;codecs=vp9,opus", "video/webm", "video/mp4"];
const escolherMime = (lista = MIMES) => {
  if (typeof MediaRecorder === "undefined") return "";
  return lista.find((m) => MediaRecorder.isTypeSupported(m)) ?? "";
};
/* 1,2 Mbps: legível para ler documento compartilhado sem virar meio giga por hora. */
const VIDEO_BPS = 1_200_000;

/** Espera um gravador terminar de despejar o último pedaço. */
const aoParar = (rec: MediaRecorder | null) =>
  new Promise<void>((pronto) => {
    if (!rec || rec.state === "inactive") return pronto();
    rec.addEventListener("stop", () => pronto(), { once: true });
    /* Cinto: gravador que não avisa não pode pendurar o envio da reunião. */
    setTimeout(pronto, 3000);
  });
const extensaoDo = (mime: string) => (String(mime).includes("mp4") ? "m4a" : "webm");

/**
 * Pico da trilha em dBFS, para o número ao lado do medidor.
 *
 * A barra colorida responde "está pegando?"; o número responde "está BAIXO?" —
 * reunião gravada a −40 dB transcreve mal, e a pessoa só descobriria no texto.
 */
const dbDo = (barras: number[]) => {
  const pico = Math.max(...barras, 0);
  return pico <= 0.002 ? null : Math.round(20 * Math.log10(pico));
};

/** Média de uma faixa do espectro, normalizada em 0..1. */
const barrasDo = (analisador: AnalyserNode, dados: Uint8Array) => {
  analisador.getByteFrequencyData(dados as never);
  const fatia = Math.floor(dados.length / BARRAS) || 1;
  return Array.from({ length: BARRAS }, () => 0).map((_, i) => {
    let soma = 0;
    for (let j = 0; j < fatia; j++) soma += dados[i * fatia + j] || 0;
    return Math.min(1, soma / fatia / 180);
  });
};

export type Quadro = { segundo: number; blob: Blob };
export type Gravadas = {
  mic?: File;
  aba?: File;
  /** Mistura das duas: o arquivo que se ouve depois. */
  audioMix?: File | null;
  /** A aba em imagem, com a mistura das vozes. Só quando o escritório pediu. */
  video?: File | null;
  quadros: Quadro[];
  marcadores: { segundo: number; rotulo: string }[];
  duracaoSeg: number;
};

export type Rotulo = "mic" | "aba";

export function useGravacaoReuniao({
  aoTerminar,
  aoTranscreverPedaco,
  config,
}: {
  aoTerminar: (g: Gravadas) => Promise<unknown>;
  aoTranscreverPedaco?: (arquivo: File) => Promise<string>;
  config: ConfigGravacao;
}) {
  const ajustesRef = useRef(config);
  ajustesRef.current = config;

  const [estado, setEstado] = useState<"ocioso" | "preparando" | "gravando" | "enviando">("ocioso");
  const [segundos, setSegundos] = useState(0);
  const [niveis, setNiveis] = useState<Record<Rotulo, number[]>>({ mic: VAZIO(), aba: VAZIO() });
  const [ganho, setGanho] = useState<Record<Rotulo, number | null>>({ mic: null, aba: null });
  const [aviso, setAviso] = useState("");
  const [erro, setErro] = useState("");
  const [pausado, setPausado] = useState(false);
  const [marcadores, setMarcadores] = useState<{ segundo: number; rotulo: string }[]>([]);
  const [previa, setPrevia] = useState<string[]>([]);

  const gravadores = useRef<{ rotulo: Rotulo; rec: MediaRecorder; pedacos: Blob[] }[]>([]);
  const fontes = useRef<MediaStream[]>([]);
  const audioCtx = useRef<AudioContext | null>(null);
  const analises = useRef<{ rotulo: Rotulo; analisador: AnalyserNode; dados: Uint8Array }[]>([]);
  const quadroAnim = useRef<number | null>(null);
  const montado = useRef(true);
  const aoTerminarRef = useRef(aoTerminar);
  const aoTranscreverRef = useRef(aoTranscreverPedaco);
  useEffect(() => {
    aoTerminarRef.current = aoTerminar;
    aoTranscreverRef.current = aoTranscreverPedaco;
  });

  /* Amostragem da imagem da aba, para ler o nome de quem fala. */
  const video = useRef<HTMLVideoElement | null>(null);
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const amostra = useRef<ReturnType<typeof setInterval> | null>(null);
  const quadros = useRef<Quadro[]>([]);
  const assinatura = useRef("");
  const nivelAba = useRef(0);
  /* Pico observado por trilha durante a gravação inteira. Trilha que nunca passou
     do silêncio NÃO sobe: o Whisper, em áudio mudo, não devolve vazio — devolve
     texto decorado do treino. Não mandar é a única forma de não receber invenção. */
  const pico = useRef<Record<Rotulo, number>>({ mic: 0, aba: 0 });

  /* Prévia ao vivo: gravador PRÓPRIO, reiniciado a cada janela. Pedaço do meio de
     um webm não é decodificável sozinho (o cabeçalho está no primeiro), então
     reaproveitar os chunks do gravador principal daria arquivo inválido. */
  const previaRec = useRef<MediaRecorder | null>(null);
  const previaTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const mistura = useRef<{ ctx: AudioContext } | null>(null);

  /* Mistura das duas trilhas — o arquivo que se ouve depois. Um gravador só, na
     soma de mic + aba: guardar as duas separadas e tocar em paralelo sai de
     sincronia no navegador em segundos, e o destaque de palavra depende de UM
     relógio. Ela pausa e retoma junto das outras, senão o áudio ficaria mais longo
     que a transcrição e o destaque andaria adiantado depois da primeira pausa. */
  const mixRec = useRef<MediaRecorder | null>(null);
  const mixPedacos = useRef<Blob[]>([]);
  const videoRec = useRef<MediaRecorder | null>(null);
  const videoPedacos = useRef<Blob[]>([]);

  /* Pausa e marcadores em ref: o intervalo de amostragem e o `onstop` rodam fora
     do React e não enxergam o estado desta renderização. */
  const pausadoRef = useRef(false);
  pausadoRef.current = pausado;
  const marcadoresRef = useRef<{ segundo: number; rotulo: string }[]>([]);
  marcadoresRef.current = marcadores;
  const segundosRef = useRef(0);
  segundosRef.current = segundos;

  /*
   * Gravar exige MediaRecorder e ALGUMA fonte. `getDisplayMedia` não pode ser o
   * requisito: agora o gravador mora dentro do atendimento, onde a reunião é o
   * advogado ao telefone — exigir captura de aba deixaria o Firefox, o Safari e
   * qualquer celular sem gravação nenhuma, que é o que o gravador antigo fazia
   * com um `getUserMedia` sozinho.
   */
  const suportado =
    typeof navigator !== "undefined" &&
    typeof MediaRecorder !== "undefined" &&
    Boolean(navigator.mediaDevices?.getUserMedia ?? navigator.mediaDevices?.getDisplayMedia);

  /*
   * Fora de HTTPS o navegador ESCONDE `mediaDevices` inteiro — e aí `suportado`
   * fica falso e a tela acusava o navegador ("este navegador não grava áudio")
   * por um problema que é do endereço. Quem abre a mesa por `http://crm:8787`
   * ficava caçando extensão e trocando de navegador sem chance de acertar.
   */
  const seguro = typeof window === "undefined" || window.isSecureContext;

  const soltarTudo = useCallback(() => {
    if (quadroAnim.current) cancelAnimationFrame(quadroAnim.current);
    quadroAnim.current = null;
    if (amostra.current) clearInterval(amostra.current);
    amostra.current = null;
    for (const a of analises.current) {
      try {
        a.analisador.disconnect();
      } catch {
        /* ignore */
      }
    }
    analises.current = [];
    try {
      audioCtx.current?.close();
    } catch {
      /* ignore */
    }
    audioCtx.current = null;
    try {
      mistura.current?.ctx.close();
    } catch {
      /* ignore */
    }
    mistura.current = null;
    if (video.current) {
      try {
        video.current.pause();
        video.current.srcObject = null;
      } catch {
        /* ignore */
      }
      video.current = null;
    }
    canvas.current = null;
    if (previaTimer.current) clearInterval(previaTimer.current);
    previaTimer.current = null;
    try {
      if (previaRec.current?.state !== "inactive") previaRec.current?.stop();
    } catch {
      /* ignore */
    }
    previaRec.current = null;
    for (const s of fontes.current) s.getTracks().forEach((t) => t.stop());
    fontes.current = [];
  }, []);

  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
      soltarTudo();
    };
  }, [soltarTudo]);

  /* Relógio: sem ele ninguém sabe se ainda está gravando. Pausado, não anda. */
  useEffect(() => {
    if (estado !== "gravando" || pausado) return;
    const t = setInterval(() => setSegundos((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [estado, pausado]);

  const parar = useCallback(() => {
    for (const extra of [mixRec.current, videoRec.current]) {
      try {
        if (extra && (extra.state === "recording" || extra.state === "paused")) extra.stop();
      } catch {
        /* ignore */
      }
    }
    for (const g of gravadores.current) if (g.rec.state === "recording" || g.rec.state === "paused") g.rec.stop();
  }, []);

  /**
   * Pausar de verdade (`MediaRecorder.pause`), não parar e recomeçar.
   *
   * Parar e recomeçar geraria DOIS arquivos por trilha e a linha do tempo das duas
   * deixaria de bater — a única coisa que sustenta a atribuição de quem falou o
   * quê. Pausado, o relógio também para: senão a régua fica com um buraco que a
   * transcrição não tem.
   */
  const alternarPausa = useCallback(() => {
    const lista = gravadores.current;
    if (!lista.length) return false;
    const vaiPausar = lista[0].rec.state === "recording";
    for (const rec of [...lista.map((g) => g.rec), mixRec.current, videoRec.current].filter(Boolean) as MediaRecorder[]) {
      try {
        if (vaiPausar && rec.state === "recording") rec.pause();
        if (!vaiPausar && rec.state === "paused") rec.resume();
      } catch {
        /* navegador sem pause: segue gravando, e o botão volta ao normal */
      }
    }
    /* Confirma pelo estado REAL do gravador: navegador sem `pause` continua
       gravando, e o botão não pode dizer "pausado" nesse caso. */
    const agoraPausado = lista[0].rec.state === "paused";
    setPausado(agoraPausado);
    return agoraPausado;
  }, []);

  /**
   * Marca o instante atual.
   *
   * Na reunião a pessoa SABE quando algo importa (o cliente disse a data da
   * dispensa) e depois não acha esse ponto em cinquenta minutos de transcrição.
   * Devolve o segundo para quem chamou poder confirmar na tela: marcar sem retorno
   * visível é indistinguível de marcar sem funcionar.
   */
  const marcar = useCallback((rotulo = "") => {
    const segundo = segundosRef.current;
    setMarcadores((atuais) => [...atuais, { segundo, rotulo: String(rotulo).slice(0, 120) }]);
    return segundo;
  }, []);

  /**
   * Um medidor por trilha, no mesmo AudioContext.
   *
   * Com um medidor só, "está pegando áudio?" era respondido pela metade: a barra
   * pulava com a própria voz e a reunião saía sem o outro lado. Aqui dá para ver,
   * antes de a reunião acabar, que a aba está muda.
   */
  const medirNiveis = useCallback((porRotulo: Partial<Record<Rotulo, MediaStream | null>>) => {
    try {
      const ctx = new (
        window.AudioContext ?? (window as never as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
      )();
      audioCtx.current = ctx;

      analises.current = (Object.entries(porRotulo) as [Rotulo, MediaStream | null][])
        .filter(([, stream]) => stream?.getAudioTracks().length)
        .map(([rotulo, stream]) => {
          const analisador = ctx.createAnalyser();
          analisador.fftSize = 256;
          ctx.createMediaStreamSource(stream!).connect(analisador);
          return { rotulo, analisador, dados: new Uint8Array(analisador.frequencyBinCount) };
        });

      /* ~70ms entre atualizações de ESTADO (não de leitura): a 60fps o React
         re-renderizava a tela inteira sessenta vezes por segundo — caro, e é o que
         fazia o clique em pausar/marcar se perder no meio de uma remontagem. */
      let ultimoDesenho = 0;
      const passo = () => {
        if (!analises.current.length) return;
        const agora = performance.now();
        if (agora - ultimoDesenho < 70) {
          quadroAnim.current = requestAnimationFrame(passo);
          return;
        }
        ultimoDesenho = agora;
        const proximos: Record<Rotulo, number[]> = { mic: VAZIO(), aba: VAZIO() };
        for (const a of analises.current) proximos[a.rotulo] = barrasDo(a.analisador, a.dados);
        nivelAba.current = Math.max(...proximos.aba, 0);
        pico.current = {
          mic: Math.max(pico.current.mic, ...proximos.mic),
          aba: Math.max(pico.current.aba, ...proximos.aba),
        };
        setNiveis(proximos);
        setGanho({ mic: dbDo(proximos.mic), aba: dbDo(proximos.aba) });
        quadroAnim.current = requestAnimationFrame(passo);
      };
      passo();
    } catch {
      /* medidor é enfeite: falhar aqui não pode derrubar a gravação */
    }
  }, []);

  /**
   * Amostra a imagem da aba para descobrir QUEM está falando.
   *
   * Dois filtros antes de guardar o quadro, porque quadro à toa é chamada de modelo
   * paga à toa:
   *  - a aba tem de estar com som (ninguém falando = nada a atribuir);
   *  - a imagem tem de ter mudado desde a última amostra (tela parada quer dizer
   *    mesmo falante, ou apresentação sem ninguém falando).
   */
  const amostrarAba = useCallback((stream: MediaStream) => {
    if (!stream.getVideoTracks().length) return;
    const intervaloMs = Math.max(2, Number(ajustesRef.current.intervaloQuadro) || 4) * 1000;
    try {
      const el = document.createElement("video");
      el.muted = true;
      el.playsInline = true;
      el.srcObject = new MediaStream(stream.getVideoTracks());
      void el.play().catch(() => {});
      video.current = el;

      const tela = document.createElement("canvas");
      canvas.current = tela;

      amostra.current = setInterval(() => {
        if (quadros.current.length >= MAX_QUADROS) return;
        /* Pausado não se amostra: a tela continua mudando e o quadro entraria num
           instante que o áudio não tem. */
        if (pausadoRef.current) return;
        if (nivelAba.current < SILENCIO) return;
        const largura = el.videoWidth;
        const altura = el.videoHeight;
        if (!largura || !altura) return;

        const escala = Math.min(1, LARGURA_QUADRO / largura);
        tela.width = Math.round(largura * escala);
        tela.height = Math.round(altura * escala);
        tela.getContext("2d")!.drawImage(el, 0, 0, tela.width, tela.height);

        /* Assinatura barata da imagem: 8x8 em tons de cinza. Responde "mudou algo
           na tela?" sem carregar biblioteca nenhuma. */
        const mini = document.createElement("canvas");
        mini.width = 8;
        mini.height = 8;
        const mctx = mini.getContext("2d")!;
        mctx.drawImage(tela, 0, 0, 8, 8);
        const px = mctx.getImageData(0, 0, 8, 8).data;
        let atual = "";
        for (let i = 0; i < px.length; i += 4) {
          atual += String.fromCharCode(65 + Math.round((px[i] * 0.3 + px[i + 1] * 0.59 + px[i + 2] * 0.11) / 32));
        }
        if (atual === assinatura.current) return;
        assinatura.current = atual;

        tela.toBlob(
          (blob) => {
            if (!blob) return;
            /*
             * MESMO relógio do áudio (`segundosRef`), não o relógio de parede: com
             * `Date.now()`, uma pausa de três minutos deixava todo quadro seguinte
             * três minutos ADIANTE da linha do tempo da transcrição — e o nome lido
             * era colado na fala errada.
             */
            quadros.current.push({ segundo: segundosRef.current, blob });
          },
          "image/jpeg",
          0.6,
        );
      }, intervaloMs);
    } catch {
      /* ler nome é enriquecimento: falhar aqui não pode derrubar a gravação */
    }
  }, []);

  /**
   * Prévia ao vivo: transcreve janelas curtas enquanto a reunião corre.
   *
   * Mistura mic + aba num stream só (não é a gravação — é amostra) e reinicia um
   * gravador a cada janela, porque pedaço do meio de um webm não abre sozinho. Cada
   * janela vai ao servidor e volta como texto.
   *
   * Custa uma transcrição por janela, então é opcional. O valor é não terminar uma
   * reunião de uma hora para descobrir que o áudio estava mudo — e é a prévia, não
   * a gravação, que fica com o prejuízo se falhar.
   */
  const ligarPrevia = useCallback((entradas: (MediaStream | null)[], janelaSeg: number) => {
    const transcrever = aoTranscreverRef.current;
    if (!transcrever) return;
    try {
      const ctx = new (
        window.AudioContext ?? (window as never as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
      )();
      const destino = ctx.createMediaStreamDestination();
      for (const f of entradas.filter(Boolean) as MediaStream[]) {
        if (f.getAudioTracks().length) ctx.createMediaStreamSource(f).connect(destino);
      }
      mistura.current = { ctx };

      const mime = escolherMime();
      const abrirJanela = () => {
        const rec = new MediaRecorder(destino.stream, mime ? { mimeType: mime } : undefined);
        const pedacos: Blob[] = [];
        rec.ondataavailable = (e) => e.data?.size && pedacos.push(e.data);
        rec.onstop = async () => {
          const blob = new Blob(pedacos, { type: mime || "audio/webm" });
          /* Janela quase vazia é silêncio: mandar isso é pagar transcrição para
             receber string vazia (ou invenção). */
          if (blob.size < 8000) return;
          try {
            const texto = await transcrever(new File([blob], `previa-${pedacos.length}.webm`, { type: blob.type }));
            if (texto && montado.current) setPrevia((atual) => [...atual, texto]);
          } catch {
            /* prévia é conforto: falhar não pode aparecer como erro da gravação */
          }
        };
        previaRec.current = rec;
        rec.start();
      };

      abrirJanela();
      previaTimer.current = setInterval(
        () => {
          try {
            if (previaRec.current?.state === "recording") previaRec.current.stop();
            abrirJanela();
          } catch {
            /* ignore */
          }
        },
        Math.max(20, Number(janelaSeg) || 45) * 1000,
      );
    } catch {
      /* sem AudioContext não há prévia, e a gravação segue igual */
    }
  }, []);

  const gravar = useCallback(async () => {
    setErro("");
    setAviso("");
    quadros.current = [];
    assinatura.current = "";
    setMarcadores([]);
    setPrevia([]);
    setPausado(false);
    pico.current = { mic: 0, aba: 0 };
    mixPedacos.current = [];
    videoPedacos.current = [];
    videoRec.current = null;
    const ajuste = ajustesRef.current;
    /* Navegador sem captura de aba grava o microfone e diz por quê, em vez de
       recusar a reunião inteira por causa de uma preferência. */
    const podeAba = ajuste.gravarAba && Boolean(navigator.mediaDevices?.getDisplayMedia);
    if (ajuste.gravarAba && !podeAba && !ajuste.gravarMic) {
      setErro("Este navegador não captura o áudio de outra aba. Escolha “Só meu microfone” nos ajustes da gravação.");
      return;
    }
    if (!ajuste.gravarMic && !ajuste.gravarAba) {
      setErro("As duas fontes estão desligadas nos ajustes — ligue o microfone ou o áudio da aba.");
      return;
    }
    setEstado("preparando");

    try {
      /* O vídeo é pedido junto porque o navegador não abre o seletor de aba só para
         áudio — e ele FICA quando a leitura de nome está ligada: é dele que sai o
         nome de quem falou. Desligada, a trilha de vídeo morre na hora. */
      if (ajuste.gravarAba && !podeAba) setAviso("Este navegador não captura o áudio da aba — gravando só o seu microfone.");
      const daAba = podeAba
        ? await navigator.mediaDevices.getDisplayMedia({
            video: true,
            audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
          })
        : null;
      if (daAba) {
        fontes.current.push(daAba);
        /* A trilha de imagem serve a dois fins independentes — ler o nome de quem
           fala e guardar o vídeo. Ela só morre quando NENHUM dos dois foi pedido. */
        if (!ajuste.lerNomes && !ajuste.guardarVideo) daAba.getVideoTracks().forEach((t) => t.stop());
      }

      const doMicrofone = ajuste.gravarMic
        ? await navigator.mediaDevices
            .getUserMedia({
              audio: {
                echoCancellation: true,
                noiseSuppression: true,
                ...(ajuste.microfoneId ? { deviceId: { exact: ajuste.microfoneId } } : {}),
              },
            })
            /* Microfone salvo pode não existir nesta máquina: cai no padrão do
               sistema em vez de a gravação falhar por causa da preferência. */
            .catch(() =>
              navigator.mediaDevices
                .getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } })
                .catch(() => null),
            )
        : null;
      if (doMicrofone) fontes.current.push(doMicrofone);

      const temAba = Boolean(daAba?.getAudioTracks().length);
      if (!temAba && !doMicrofone) {
        soltarTudo();
        setEstado("ocioso");
        setErro('Nenhum áudio foi capturado. Ao escolher a aba, marque "Compartilhar áudio da aba".');
        return;
      }
      if (!temAba && podeAba) {
        setAviso('Sem áudio da aba — gravando só o seu microfone. Para pegar os dois lados, marque "Compartilhar áudio da aba".');
      }

      const mime = escolherMime();
      gravadores.current = [];
      const abrir = (rotulo: Rotulo, fonte: MediaStream) => {
        const so = new MediaStream(fonte.getAudioTracks());
        const rec = new MediaRecorder(so, {
          ...(mime ? { mimeType: mime } : {}),
          audioBitsPerSecond: Number(ajuste.bitrate) || 64_000,
        });
        const pedacos: Blob[] = [];
        rec.ondataavailable = (e) => e.data?.size && pedacos.push(e.data);
        gravadores.current.push({ rotulo, rec, pedacos });
      };
      if (temAba) abrir("aba", daAba!);
      if (doMicrofone) abrir("mic", doMicrofone);

      medirNiveis({ aba: temAba ? daAba : null, mic: doMicrofone });

      /* Mistura das fontes num gravador só — é o áudio que fica para ouvir. Roda em
         paralelo, na MESMA janela de tempo das trilhas. Se falhar, a reunião
         continua (transcrição é o registro; áudio é conforto). */
      const trilhaDeImagem = ajuste.guardarVideo ? (daAba?.getVideoTracks() ?? [])[0] : undefined;
      if (ajuste.guardarAudio || trilhaDeImagem) {
        try {
          const ctxMix = new (
            window.AudioContext ?? (window as never as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
          )();
          const destino = ctxMix.createMediaStreamDestination();
          for (const f of [temAba ? daAba : null, doMicrofone].filter(Boolean) as MediaStream[]) {
            if (f.getAudioTracks().length) ctxMix.createMediaStreamSource(f).connect(destino);
          }
          mistura.current = { ctx: ctxMix };

          if (ajuste.guardarAudio) {
            const recMix = new MediaRecorder(destino.stream, {
              ...(mime ? { mimeType: mime } : {}),
              audioBitsPerSecond: Number(ajuste.bitrate) || 64_000,
            });
            recMix.ondataavailable = (e) => e.data?.size && mixPedacos.current.push(e.data);
            mixRec.current = recMix;
            recMix.start(5_000);
          }

          /*
           * O vídeo leva a MISTURA como som, não o áudio da aba: assistir de novo
           * uma reunião em que só o cliente se ouve é pior que não ter vídeo — a
           * pergunta do advogado é metade do que faz a resposta valer.
           */
          if (trilhaDeImagem) {
            const mimeVideo = escolherMime(MIMES_VIDEO);
            const recVideo = new MediaRecorder(new MediaStream([trilhaDeImagem, ...destino.stream.getAudioTracks()]), {
              ...(mimeVideo ? { mimeType: mimeVideo } : {}),
              videoBitsPerSecond: VIDEO_BPS,
              audioBitsPerSecond: Number(ajuste.bitrate) || 64_000,
            });
            recVideo.ondataavailable = (e) => e.data?.size && videoPedacos.current.push(e.data);
            videoRec.current = recVideo;
            recVideo.start(5_000);
          }
        } catch {
          /* Um dos dois pode ter começado antes de o outro estourar: gravador
             solto continuaria consumindo a máquina até a aba fechar. */
          for (const orfao of [mixRec.current, videoRec.current]) {
            try {
              if (orfao?.state === "recording") orfao.stop();
            } catch {
              /* ignore */
            }
          }
          mixRec.current = null;
          videoRec.current = null;
        }
      }

      if (temAba && ajuste.lerNomes) amostrarAba(daAba!);
      if (ajuste.previaAoVivo) ligarPrevia([temAba ? daAba : null, doMicrofone], ajuste.janelaPrevia);

      /* O último a parar fecha a conta: só então existe arquivo de cada fonte. */
      let restantes = gravadores.current.length;
      for (const g of gravadores.current) {
        g.rec.onstop = async () => {
          if (--restantes > 0) return;

          const capturados = quadros.current.slice();
          quadros.current = [];
          const feitos = gravadores.current.slice();
          gravadores.current = [];
          soltarTudo();
          if (!montado.current) return;
          setNiveis({ mic: VAZIO(), aba: VAZIO() });
          setEstado("enviando");

          const extensao = extensaoDo(mime || "audio/webm");
          const carimbo = new Date().toISOString().slice(0, 16).replace(":", "h");
          const arquivos: { mic?: File; aba?: File } = {};
          const mudas: Rotulo[] = [];
          for (const feito of feitos) {
            const blob = new Blob(feito.pedacos, { type: mime || "audio/webm" });
            if (blob.size < 2000) continue;
            /* Nunca passou de ~−34 dB (0,02 na escala do medidor) em toda a reunião:
               é silêncio. Mandar silêncio para o Whisper volta como texto INVENTADO
               — não mandar é a única forma de não receber. */
            if ((pico.current[feito.rotulo] || 0) < 0.02) {
              mudas.push(feito.rotulo);
              continue;
            }
            arquivos[feito.rotulo] = new File([blob], `reuniao-${feito.rotulo}-${carimbo}.${extensao}`, { type: blob.type });
          }

          if (!arquivos.mic && !arquivos.aba) {
            setEstado("ocioso");
            setErro(
              mudas.length
                ? "Nenhuma das trilhas tinha fala audível — nada foi enviado. (Trilha muda transcrita volta como texto inventado, então ela é descartada aqui.)"
                : "A gravação saiu vazia — nada foi enviado.",
            );
            return;
          }
          if (mudas.length) {
            setAviso(
              `Trilha sem fala audível descartada: ${mudas
                .map((m) => (m === "aba" ? "áudio da aba" : "microfone"))
                .join(" e ")}. Só o que tinha som foi transcrito.`,
            );
          }

          /* A mistura e o vídeo viram arquivo aqui — e SÓ depois de os gravadores
             deles avisarem que pararam: eles gravam em pedaços de 5s, e ler os
             pedaços antes do último despejo cortava o fim da reunião. */
          await Promise.all([aoParar(mixRec.current), aoParar(videoRec.current)]);

          /* Menos de 2 KB é silêncio: não sobe. */
          let audioMix: File | null = null;
          try {
            const blobMix = new Blob(mixPedacos.current, { type: mime || "audio/webm" });
            mixPedacos.current = [];
            if (blobMix.size > 2000) audioMix = new File([blobMix], `reuniao-${carimbo}.${extensao}`, { type: blobMix.type });
          } catch {
            audioMix = null;
          }

          let telaGravada: File | null = null;
          try {
            const pedacosVideo = videoPedacos.current;
            videoPedacos.current = [];
            if (pedacosVideo.length) {
              const tipo = videoRec.current?.mimeType || "video/webm";
              const blobVideo = new Blob(pedacosVideo, { type: tipo });
              /* 20 KB: menos que isso é cabeçalho de webm sem imagem dentro. */
              if (blobVideo.size > 20_000) {
                telaGravada = new File([blobVideo], `reuniao-${carimbo}-tela.${/mp4/.test(tipo) ? "mp4" : "webm"}`, {
                  type: tipo,
                });
              }
            }
          } catch {
            telaGravada = null;
          }
          videoRec.current = null;

          try {
            await aoTerminarRef.current({
              ...arquivos,
              audioMix,
              video: telaGravada,
              quadros: capturados,
              marcadores: marcadoresRef.current,
              /* Duração = relógio da GRAVAÇÃO, não de parede: com pausa, o de parede
                 fica maior que o áudio e a régua "quem falou quando" esticava. */
              duracaoSeg: segundosRef.current,
            });
          } catch (e) {
            if (montado.current) setErro(e instanceof Error ? e.message : "Não deu para enviar a gravação.");
          } finally {
            if (montado.current) setEstado("ocioso");
          }
        };
      }

      /* A pessoa pode encerrar pelo aviso do próprio navegador. */
      for (const t of daAba?.getTracks() ?? []) t.onended = parar;

      for (const g of gravadores.current) g.rec.start(5_000);
      setSegundos(0);
      setEstado("gravando");
    } catch (e) {
      soltarTudo();
      const msg = e instanceof Error ? e.message : String(e);
      /* Cancelar o seletor de aba não é erro: é a pessoa mudando de ideia. */
      setErro(/Permission denied|NotAllowed|dismissed|aborted/i.test(msg) ? "" : msg);
      setEstado("ocioso");
    }
  }, [amostrarAba, ligarPrevia, medirNiveis, parar, soltarTudo]);

  return {
    estado,
    segundos,
    niveis,
    ganho,
    pausado,
    marcadores,
    previa,
    aviso,
    erro,
    suportado,
    seguro,
    gravar,
    parar,
    alternarPausa,
    marcar,
  };
}
